import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { renderMarkdown } from './markdownUtils';

export default function InsightsChat({ projectId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const scrollRef               = useRef(null);

  useEffect(() => { setMessages([]); setInput(''); }, [projectId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const send = async (question) => {
    if (!question.trim() || loading || !projectId) return;
    const q = question.trim();
    setInput('');
    const next = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setLoading(true);
    try {
      const res = await api.post(`/api/projects/${projectId}/chat`, {
        messages,
        question: q,
      });
      const answer = res.data?.answer || '_(no response — please try again)_';
      setMessages([...next, { role: 'assistant', content: answer }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Something went wrong. Please try again.';
      setMessages([...next, { role: 'assistant', content: `⚠️ ${msg}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  return (
    <div style={{
      marginTop: 20,
      borderRadius: 10,
      border: '1.5px solid #d4def0',
      background: 'linear-gradient(135deg, #f4f7fd 0%, #eef2fb 100%)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '12px 16px 10px',
        borderBottom: messages.length > 0 || loading ? '1px solid #dce6f5' : 'none',
      }}>
        <span style={{ fontSize: 16 }}>💬</span>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#142D55' }}>Continue the conversation</div>
          <div style={{ fontSize: 11, color: '#6b7a8d', marginTop: 1 }}>
            Answer the questions above, or ask your own
          </div>
        </div>
        {messages.length > 0 && (
          <button
            onClick={() => setMessages([])}
            disabled={loading}
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7a8d' }}
            title="Clear conversation"
          >
            Clear
          </button>
        )}
      </div>

      {/* Message history */}
      {(messages.length > 0 || loading) && (
        <div ref={scrollRef} style={{ maxHeight: 380, overflowY: 'auto', padding: '12px 16px 4px' }}>
          {messages.map((m, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 10,
            }}>
              <div style={{
                maxWidth: '82%',
                padding: '9px 14px',
                borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: m.role === 'user' ? '#3E68B2' : '#fff',
                color: m.role === 'user' ? '#fff' : '#2c3e50',
                fontSize: 13,
                lineHeight: 1.55,
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
              <div style={{
                padding: '10px 16px',
                borderRadius: '16px 16px 16px 4px',
                background: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: '#6b7a8d',
                fontSize: 13,
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                <div className="spinner" style={{ width: 14, height: 14 }} />
                Analysing data…
              </div>
            </div>
          )}
        </div>
      )}

      {/* Input row */}
      <div style={{ padding: '10px 16px 12px', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKey}
          disabled={loading}
          placeholder="Answer the questions above, or ask something else…"
          rows={1}
          style={{
            flex: 1,
            resize: 'none',
            border: '1.5px solid #c8d8f0',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 13,
            color: '#2c3e50',
            fontFamily: 'inherit',
            outline: 'none',
            lineHeight: 1.5,
            background: loading ? '#f0f4fb' : '#fff',
            transition: 'border-color 0.15s',
          }}
          onFocus={e => { e.target.style.borderColor = '#3E68B2'; }}
          onBlur={e => { e.target.style.borderColor = '#c8d8f0'; }}
        />
        <button
          onClick={() => send(input)}
          disabled={!input.trim() || loading}
          className="btn btn-primary btn-sm"
          style={{ flexShrink: 0, padding: '8px 18px' }}
        >
          Send
        </button>
      </div>
    </div>
  );
}
